import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table/interface";
import { DateTime } from "luxon";
import { App } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import BaseButton from "@/components_v2/Button/BaseButton";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { DomainTableProps } from "@/features/organizations/profile/types";
import {
  useDeleteOrganizationDomainByIdMutation,
  useLazyGetOrganizationDomainsQuery,
} from "@/redux/service";
import { OrganizationDomainResponse } from "@/redux/organizations/types";
import DomainFormModal from "@/features/organizations/profile/components/DomainFormModal";

import "../styles.css";

const DomainActionsCell = ({
  domain,
}: {
  domain: OrganizationDomainResponse;
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [deleteOrganizationDomainById] =
    useDeleteOrganizationDomainByIdMutation();
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const handleDelete = () => {
    deleteOrganizationDomainById(domain.id)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.domainDeleted"),
        });
        setOpenConfirmDeleteModal(false);
      });
  };

  return (
    <>
      <DropdownMenu
        content={
          <div>
            <BaseButton
              className="dropdown-menu-button"
              isFullWidth
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<EditOutlined />}
              onClick={() => setOpenEditModal(true)}
            >
              {t("organizations.labels.edit")}
            </BaseButton>

            <BaseButton
              className="dropdown-menu-button"
              isFullWidth
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<DeleteOutlined />}
              onClick={() => setOpenConfirmDeleteModal(true)}
            >
              {t("organizations.labels.delete")}
            </BaseButton>
          </div>
        }
        placement="bottom"
      >
        <BaseButton
          variant={BUTTON_VARIANTS.LINK}
          size={BUTTON_SIZES.SMALL}
          leadingIcon={<EllipsisOutlined />}
        />
      </DropdownMenu>

      <DomainFormModal
        open={openEditModal}
        setOpen={setOpenEditModal}
        domain={domain}
      />

      <CancelConfirmationModal
        open={openConfirmDeleteModal}
        onCancel={() => setOpenConfirmDeleteModal(false)}
        onConfirm={handleDelete}
        title={t("organizations.labels.confirmDomainDelete")}
        confirmButtonText={t("organizations.labels.delete")}
        content={t("organizations.labels.confirmDeleteDomain", {
          domain: domain.domain,
        })}
      />
    </>
  );
};

const DomainTable = ({
  columnNames,
  defaultFilters = {},
  description,
  additionalColumns,
  ...props
}: DomainTableProps<OrganizationDomainResponse>) => {
  const { t } = useTranslation();

  const [openCreateModal, setOpenCreateModal] = useState(false);

  const columns: Record<
    string,
    ColumnType<OrganizationDomainResponse>
  > = useMemo(() => {
    const predefinedColumns: Record<
      string,
      ColumnType<OrganizationDomainResponse>
    > = {
      domain: {
        title: t("organizations.labels.domain"),
        key: "domain",
        dataIndex: "domain",
      },
      createdAt: {
        title: t("organizations.labels.createdAt"),
        key: "createdAt",
        dataIndex: "createdAt",
        render: (value: string) =>
          DateTime.fromISO(value).toFormat("dd MMMM yyyy"),
      },
      actions: {
        title: t("organizations.labels.actions"),
        key: "actions",
        render: (domain: OrganizationDomainResponse) => (
          <DomainActionsCell domain={domain} />
        ),
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, [additionalColumns]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) =>
          columns[columnKey] as ColumnType<OrganizationDomainResponse>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="table-container-layout">
      <div className="flex justify-between items-center gap-200">
        {description ? (
          <p className="body-m-regular">{description}</p>
        ) : (
          <span />
        )}
        <BaseButton
          onClick={() => setOpenCreateModal(true)}
          leadingIcon={<PlusOutlined />}
        >
          {t("organizations.labels.addDomain")}
        </BaseButton>
      </div>

      <QueryTable<OrganizationDomainResponse>
        query={useLazyGetOrganizationDomainsQuery}
        filters={defaultFilters}
        columns={mappedColumns}
        pagination={{ defaultPageSize: 5, showSizeChanger: false }}
        {...props}
      />

      <DomainFormModal
        open={openCreateModal}
        setOpen={setOpenCreateModal}
        organization={defaultFilters.organization}
      />
    </div>
  );
};

export default DomainTable;
